import React, { useMemo, useEffect, useState } from 'react';
import Papa from 'papaparse';
import { useHistory, useWarehouses } from '../api/hooks';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import {
  CalendarDays,
  Search,
  Warehouse,
  Filter,
  Table,
  Download,
  BarChartBig,
  Building2
} from 'lucide-react';

function HistoryPage() {
  const { data: historyData = [], isLoading: loadingHistory } = useHistory();
  const { data: warehouses = [], isLoading: loadingWarehouses } = useWarehouses();

  const [search, setSearch] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortField, setSortField] = useState('quantity');
  const [viewMode, setViewMode] = useState('table');
  const [groupMode, setGroupMode] = useState('month'); // 'none', 'date', 'month'

  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedChartWarehouse, setSelectedChartWarehouse] = useState('');
  const [showChart, setShowChart] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  const loading = loadingHistory || loadingWarehouses;

  const warehouseMap = useMemo(() => {
    return warehouses.reduce((map, w) => {
      map[w.storehouse_id] = w.name;
      return map;
    }, {});
  }, [warehouses]);

  const allCategories = useMemo(() => {
    const set = new Set(historyData.map(i => i.category));
    return Array.from(set);
  }, [historyData]);

  const filteredData = useMemo(() => {
    let filtered = historyData.map(item => ({
      ...item,
      warehouseName: warehouseMap[item.storehouse_id] || item.storehouse_id
    }));

    filtered = filtered.filter(item => {
      const matchProduct =
        item.product_name.toLowerCase().includes(search.toLowerCase()) ||
        String(item.product_id).includes(search);
      const matchWarehouse = !selectedWarehouse || item.warehouseName === selectedWarehouse;
      const matchCategory = !selectedCategory || item.category === selectedCategory;
      const matchStart = !startDate || item.date >= startDate;
      const matchEnd = !endDate || item.date <= endDate;
      return matchProduct && matchWarehouse && matchCategory && matchStart && matchEnd;
    });

    // Групування по датах або місяцях
    if (groupMode !== 'none') {
      const grouped = {};
      filtered.forEach(item => {
        let groupKey;
        if (groupMode === 'date') {
          // Групування по датах
          groupKey = `${item.product_id}_${item.date}_${item.storehouse_id}`;
        } else if (groupMode === 'month') {
          // Групування по місяцях (YYYY-MM)
          const yearMonth = item.date.substring(0, 7); // "2025-09-24" -> "2025-09"
          groupKey = `${item.product_id}_${yearMonth}_${item.storehouse_id}`;
        }
        
        if (!grouped[groupKey]) {
          grouped[groupKey] = {
            ...item,
            date: groupMode === 'month' ? item.date.substring(0, 7) : item.date, // Показуємо тільки YYYY-MM для місяців
            quantity: 0,
            transactions_count: 0
          };
        }
        grouped[groupKey].quantity += item.quantity;
        grouped[groupKey].transactions_count += 1;
      });
      filtered = Object.values(grouped);
    }

    filtered.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [search, startDate, endDate, selectedWarehouse, selectedCategory, historyData, warehouseMap, sortOrder, sortField, groupMode]);

  const csvUrl = useMemo(() => {
    if (filteredData.length === 0) return null;
    const csv = Papa.unparse(filteredData);
    const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
    return URL.createObjectURL(blob);
  }, [filteredData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, startDate, endDate, selectedWarehouse, selectedCategory, sortOrder, sortField]);

  const start = (currentPage - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  const paginatedData = filteredData.slice(start, end);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const toggleSort = () => {
    setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const productOptions = useMemo(() => {
    return [...new Set(historyData.map(e => `${e.product_id} — ${e.product_name}`))];
  }, [historyData]);

  const chartData = useMemo(() => {
    const matchID = selectedProduct.split(' — ')[0];
    const chartFiltered = historyData
      .filter(h =>
        String(h.product_id) === matchID &&
        (!selectedChartWarehouse || warehouseMap[h.storehouse_id] === selectedChartWarehouse)
      )
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    return {
      labels: chartFiltered.map(d => d.date),
      datasets: [{
        label: selectedProduct,
        data: chartFiltered.map(d => d.quantity),  // Зберігаємо знак (+ продаж, - повернення)
        borderColor: 'blue',
        tension: 0.1
      }]
    };
  }, [selectedProduct, selectedChartWarehouse, historyData, warehouseMap]);

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-4">Історія продажів</h2>

      {/* Filters */}
      <div className="space-y-4 mb-4">
        <div className="flex flex-wrap gap-4 items-center">
          <label className="flex items-center gap-2">
            <CalendarDays size={16} />
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="border p-2 rounded" />
          </label>
          <label className="flex items-center gap-2">
            <CalendarDays size={16} />
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="border p-2 rounded" />
          </label>
        </div>

        <div className="flex flex-wrap gap-4 items-center">
          <label className="flex items-center gap-2">
            <Warehouse size={16} />
            <select value={selectedWarehouse} onChange={e => setSelectedWarehouse(e.target.value)} className="border p-2 rounded">
              <option value="">Усі склади</option>
              {warehouses.map(w => (
                <option key={w.storehouse_id} value={w.name}>{w.name}</option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2">
            <Filter size={16} />
            <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="border p-2 rounded">
              <option value="">Усі категорії</option>
              {allCategories.sort().map((cat, i) => (
                <option key={i} value={cat}>{cat}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex flex-wrap gap-4 items-center justify-between">
          <label className="flex items-center gap-2 grow">
            <Search size={16} />
            <input
              type="text"
              placeholder="Пошук по назві або ID"
              className="border p-2 rounded w-full"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </label>

          <div className="flex gap-2 items-center">
            <select
              value={groupMode}
              onChange={e => setGroupMode(e.target.value)}
              className="border p-2 rounded bg-white"
            >
              <option value="month">📅 По місяцях</option>
              <option value="date">📆 По датах</option>
              <option value="none">📋 Деталізовано</option>
            </select>
            {csvUrl && (
              <a href={csvUrl} download="history.csv" className="bg-blue-500 text-white px-4 py-2 rounded flex items-center gap-2">
                <Download size={16} /> Завантажити CSV
              </a>
            )}
            <select
              value={viewMode}
              onChange={e => setViewMode(e.target.value)}
              className="border p-2 rounded"
            >
              <option value="table">Таблиця</option>
              <option value="chart">Графік</option>
            </select>
          </div>
        </div>
      </div>

      {/* Chart Filters */}
      {viewMode === 'chart' && (
        <div className="flex gap-3 mb-4 flex-wrap items-center">
          <div className="flex items-center border rounded px-2">
            <BarChartBig className="w-4 h-4 text-gray-500 mr-2" />
            <input
              list="products"
              className="py-2 outline-none w-64"
              placeholder="Введіть назву або ID продукту..."
              value={selectedProduct}
              onChange={e => {
                setSelectedProduct(e.target.value);
                setShowChart(false);
              }}
            />
            <datalist id="products">
              {productOptions.map(name => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </div>

          <div className="flex items-center border rounded px-2">
            <Building2 className="w-4 h-4 text-gray-500 mr-2" />
            <select
              className="py-2 outline-none w-48"
              value={selectedChartWarehouse}
              onChange={e => {
                setSelectedChartWarehouse(e.target.value);
                setShowChart(false);
              }}
            >
              <option value="">Оберіть склад</option>
              {warehouses.map(w => (
                <option key={w.storehouse_id} value={w.name}>{w.name}</option>
              ))}
            </select>
          </div>

          <button
            disabled={!selectedProduct || !selectedChartWarehouse}
            onClick={() => setShowChart(true)}
            className={`px-4 py-2 rounded text-white ${selectedProduct && selectedChartWarehouse ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-300 cursor-not-allowed'}`}
          >
            Показати графік
          </button>
        </div>
      )}

      {/* View */}
      {loading ? (
        <div className="w-full bg-gray-100 rounded-full h-2.5 mb-4 animate-pulse">
          <div className="bg-blue-600 h-2.5 rounded-full w-1/2 animate-pulse"></div>
        </div>
      ) : viewMode === 'chart' ? (
        <div className="bg-white p-6 shadow rounded">
          {showChart && selectedProduct && chartData.labels.length > 0 ? (
            <Line data={chartData} options={{
              responsive: true,
              plugins: {
                legend: { position: 'bottom' },
                title: { display: true, text: selectedProduct }
              },
              scales: {
                y: { title: { display: true, text: 'Кількість' } },
                x: { title: { display: true, text: 'Дата' } }
              }
            }} />
          ) : (
            <p className="text-center text-gray-500">Будь ласка, оберіть продукт та склад для перегляду графіка</p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="py-2 px-4 border cursor-pointer" onClick={() => { setSortField('product_id'); toggleSort(); }}>ID ↑↓</th>
                <th className="py-2 px-4 border cursor-pointer" onClick={() => { setSortField('product_name'); toggleSort(); }}>Продукт ↑↓</th>
                <th className="py-2 px-4 border cursor-pointer" onClick={() => { setSortField('category'); toggleSort(); }}>Категорія ↑↓</th>
                <th className="py-2 px-4 border cursor-pointer" onClick={() => { setSortField('quantity'); toggleSort(); }}>Кількість ↑↓</th>
                {groupMode !== 'none' && (
                  <th className="py-2 px-4 border cursor-pointer" onClick={() => { setSortField('transactions_count'); toggleSort(); }}>Транзакцій ↑↓</th>
                )}
                <th className="py-2 px-4 border cursor-pointer" onClick={() => { setSortField('date'); toggleSort(); }}>{groupMode === 'month' ? 'Місяць' : 'Дата'} ↑↓</th>
                <th className="py-2 px-4 border cursor-pointer" onClick={() => { setSortField('warehouseName'); toggleSort(); }}>Склад ↑↓</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, idx) => (
                <tr key={idx}>
                  <td className="py-1 px-2 border text-center">{item.product_id}</td>
                  <td className="py-1 px-2 border text-left">{item.product_name}</td>
                  <td className="py-1 px-2 border text-center">{item.category}</td>
                  <td className="py-1 px-2 border text-center">{item.quantity}</td>
                  {groupMode !== 'none' && (
                    <td className="py-1 px-2 border text-center">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                        {item.transactions_count}
                      </span>
                    </td>
                  )}
                  <td className="py-1 px-2 border text-center">{item.date}</td>
                  <td className="py-1 px-2 border text-center">{item.warehouseName}</td>
                </tr>
              ))}
              {paginatedData.length === 0 && (
                <tr>
                  <td colSpan={groupMode !== 'none' ? "7" : "6"} className="py-4 text-center text-gray-500">Результатів не знайдено</td>
                </tr>
              )}
            </tbody>
          </table>

          <span className="text-xs text-gray-500 mt-2 block">
            Показано {start + 1}–{Math.min(end, filteredData.length)} з {filteredData.length}
          </span>

          <div className="flex justify-between items-center mt-4 gap-4">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="bg-gray-200 px-3 py-1 rounded disabled:opacity-50"
            >
              ← Попередня
            </button>

            <div className="flex items-center gap-2">
              <span>Сторінка</span>
              <input
                type="number"
                value={currentPage}
                min={1}
                max={totalPages}
                onChange={(e) => {
                  const page = Math.min(totalPages, Math.max(1, parseInt(e.target.value) || 1));
                  setCurrentPage(page);
                }}
                className="border rounded px-2 py-1 w-16 text-center"
              />
              <span>з {totalPages}</span>
            </div>

            <button
              onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="bg-gray-200 px-3 py-1 rounded disabled:opacity-50"
            >
              Наступна →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default HistoryPage;
